interface ValidationResult {
  isValid: boolean
  errorMessage?: string
  resizedFile?: File
}

interface LogoValidationOptions {
  maxWidth: number
  maxHeight: number
  allowedFormats: string[]
}

const DEFAULT_OPTIONS: LogoValidationOptions = {
  maxWidth: 750,
  maxHeight: 375,
  allowedFormats: ["image/png"],
}

export const logoErrorMessage =
  "Invalid file. Logo must have a maximum size of 750x375px with a 2:1 ratio and be under 2MB. Please upload a valid file or contact Nuve Support if you need assistance."

const adjustImageTo2x1Ratio = (
  img: HTMLImageElement,
  originalFile: File,
  maxWidth: number,
  maxHeight: number,
): Promise<File> => {
  return new Promise(resolve => {
    const canvas = document.createElement("canvas")
    const ctx = canvas.getContext("2d")!

    let targetWidth = maxWidth
    let targetHeight = maxHeight

    let scaledWidth = img.width
    let scaledHeight = img.height

    if (scaledWidth > maxWidth || scaledHeight > maxHeight) {
      const scaleFactorByWidth = maxWidth / scaledWidth
      const scaleFactorByHeight = maxHeight / scaledHeight
      const scaleFactor = Math.min(scaleFactorByWidth, scaleFactorByHeight)

      scaledWidth = scaledWidth * scaleFactor
      scaledHeight = scaledHeight * scaleFactor
    }

    const currentRatio = scaledWidth / scaledHeight

    if (currentRatio > 2) {
      targetWidth = Math.min(scaledWidth, maxWidth)
      targetHeight = targetWidth / 2
    } else if (currentRatio < 2) {
      targetHeight = Math.min(scaledHeight, maxHeight)
      targetWidth = targetHeight * 2
    } else {
      targetWidth = scaledWidth
      targetHeight = scaledHeight
    }

    if (targetWidth > maxWidth) {
      targetWidth = maxWidth
      targetHeight = maxWidth / 2
    }
    if (targetHeight > maxHeight) {
      targetHeight = maxHeight
      targetWidth = maxHeight * 2
    }

    canvas.width = targetWidth
    canvas.height = targetHeight

    ctx.clearRect(0, 0, targetWidth, targetHeight)

    const imageWidth = Math.min(scaledWidth, targetWidth)
    const imageHeight = Math.min(scaledHeight, targetHeight)

    const imageRatio = img.width / img.height
    let drawWidth = imageWidth
    let drawHeight = imageHeight

    if (imageWidth / imageHeight > imageRatio) {
      drawWidth = imageHeight * imageRatio
    } else {
      drawHeight = imageWidth / imageRatio
    }

    const x = (targetWidth - drawWidth) / 2
    const y = (targetHeight - drawHeight) / 2

    ctx.drawImage(img, x, y, drawWidth, drawHeight)

    canvas.toBlob(
      blob => {
        if (blob) {
          const resizedFile = new File([blob], originalFile.name, {
            type: "image/png",
            lastModified: Date.now(),
          })
          resolve(resizedFile)
        } else {
          resolve(originalFile)
        }
      },
      "image/png",
      1.0,
    )
  })
}

export const validateLogoFile = (
  file: File,
  options: Partial<LogoValidationOptions> = {},
): Promise<ValidationResult> => {
  return new Promise(resolve => {
    const opts = { ...DEFAULT_OPTIONS, ...options }

    // Check file size (2MB limit)
    const maxSizeInBytes = 2 * 1024 * 1024 // 2MB
    if (file.size > maxSizeInBytes) {
      resolve({
        isValid: false,
        errorMessage: logoErrorMessage,
      })
      return
    }

    if (!opts.allowedFormats.includes(file.type)) {
      resolve({
        isValid: false,
        errorMessage: logoErrorMessage,
      })
      return
    }

    const img = new Image()
    const objectUrl = URL.createObjectURL(file)

    img.onload = async () => {
      URL.revokeObjectURL(objectUrl)

      try {
        const adjustedFile = await adjustImageTo2x1Ratio(img, file, opts.maxWidth, opts.maxHeight)
        resolve({
          isValid: true,
          resizedFile: adjustedFile,
        })
      } catch (error) {
        console.error("Error adjusting image:", error)
        resolve({
          isValid: false,
          errorMessage: logoErrorMessage,
        })
      }
    }

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      resolve({
        isValid: false,
        errorMessage: logoErrorMessage,
      })
    }

    img.src = objectUrl
  })
}
